import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSearchParams, Navigate, useLocation } from 'react-router-dom';
import AssignmentViewer from '../components/AssignmentViewer';
import LoadingSpinner from '../components/LoadingSpinner';
import { convertTo12HourFormat, formatDateToMMMDDYYYY } from '../utils/dateUtils';

const OpenFEEventSurvey = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [step, setStep] = useState('form'); // 'form', 'survey', or 'admin'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const assignmentViewerRef = useRef();
  
  // Check for admin mode
  const isAdminMode = searchParams.get('command') === 'admin';
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    grade: '',
    email: '',
    feevent_id: '',
    attendancetype: 'Onsite'
  });
  
  // Event and survey data
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [todayEventId, setTodayEventId] = useState(null);
  
  // Admin mode data
  const [submittedSurveys, setSubmittedSurveys] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [updatingSurvey, setUpdatingSurvey] = useState(null);
  const [originalUpdatedDates, setOriginalUpdatedDates] = useState({});
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [originalFieldValues, setOriginalFieldValues] = useState({});
  
  // Pre-selected event from URL params
  const eventIdFromUrl = searchParams.get('event_id');

  // Create fake user data for unauthenticated users
  const createFakeUserData = () => {
    return {
      account_id: 'survey-guest',
      first_name: 'Survey',
      last_name: 'Guest',
      primary_email: 'survey@guest.com',
      accesscards: [{
        accesscard_id: 'survey-guest-card',
        email: 'survey@guest.com',
        role: 'Guest',
        school: {
          school_id: 'guest-school',
          title: 'Guest School',
          school_name: 'Guest School'
        }
      }],
      default_accesscard_id: 'survey-guest-card'
    };
  };

  // Get user data - either from localStorage or create fake data
  const getUserData = () => {
    const token = localStorage.getItem('token');
    if (token) {
      // If user is logged in, we'll use the token for API calls
      return null; // Let axios handle the token automatically
    } else {
      // Create fake user data for unauthenticated users
      return createFakeUserData();
    }
  };

  // Create axios instance with appropriate headers
  const createAxiosInstance = () => {
    const token = localStorage.getItem('token');
    if (token) {
      // User is logged in - use normal token
      return axios.create({
        headers: { Authorization: `Bearer ${token}` }
      });
    } else {
      // User is not logged in - use fake token for API calls (only for non-admin mode)
      return axios.create({
        headers: { Authorization: 'Bearer survey-guest-token' }
      });
    }
  };

  // Check if user is authenticated for admin mode
  const isAuthenticated = () => {
    return localStorage.getItem('token') !== null;
  };

  // Handle redirect to login for admin mode
  const [redirectToLogin, setRedirectToLogin] = useState(false);

  useEffect(() => {
    fetchEvents();
    fetchAssignment();
  }, []);

  useEffect(() => {
    if (eventIdFromUrl) {
      setFormData(prev => ({ ...prev, feevent_id: eventIdFromUrl }));
      fetchEventDetails(eventIdFromUrl);
    }
  }, [eventIdFromUrl]);

  useEffect(() => {
    if (isAdminMode && selectedEvent) {
      fetchSubmittedSurveys(selectedEvent.id);
      fetchJobs(selectedEvent.id);
    }
  }, [isAdminMode, selectedEvent]);

  const fetchEvents = async () => {
    try {
      const api = createAxiosInstance();
      const response = await api.get('/api/openfeeventregistrations/events');
      setEvents(response.data.events || []);
      setTodayEventId(response.data.todayEvent);

      // Auto-select today's event if no event is pre-selected from URL
      if (!eventIdFromUrl && response.data.todayEvent) {
        setFormData(prev => ({ ...prev, feevent_id: response.data.todayEvent }));
        fetchEventDetails(response.data.todayEvent);
      }
    } catch (err) {
      setError('Failed to load events.');
    }
  };

  const fetchAssignment = async () => {
    try {
      const api = createAxiosInstance();
      const response = await api.get('/api/openfeeventregistrations/assignment');
      setAssignment(response.data.assignment);
    } catch (err) {
      setError('Failed to load survey.');
    }
  };

  const fetchEventDetails = async (eventId) => {
    try {
      const api = createAxiosInstance();
      const response = await api.get(`/api/openfeeventregistrations/event/${eventId}`);
      setSelectedEvent(response.data.event);
      
      // Fetch instructors for this event
      const instructorsResponse = await api.get(`/api/openfeeventregistrations/instructors/${eventId}`);
      setInstructors(instructorsResponse.data.instructors || []);
    } catch (err) {
      setError('Failed to load event details.');
    }
  };

  const handleEventChange = async (eventId) => {
    setFormData(prev => ({ ...prev, feevent_id: eventId }));
    if (eventId) {
      await fetchEventDetails(eventId);
      if (isAdminMode) {
        fetchSubmittedSurveys(eventId);
        fetchJobs(eventId);
      }
    } else {
      setSelectedEvent(null);
      setInstructors([]);
      if (isAdminMode) {
        setSubmittedSurveys([]);
        setJobs([]);
      }
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    
              // Validate form
     if (!formData.name.trim() || !formData.grade.trim() || !formData.feevent_id) {
       setError('Please fill in all required fields.');
       return;
     }
     
     // Validate email format if provided
     if (formData.email.trim()) {
       const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
       if (!emailRegex.test(formData.email)) {
         setError('Please enter a valid email address.');
         return;
       }
     }
    
    setError('');
    setStep('survey');
  };

  const handleSurveySubmit = async () => {
    if (!assignmentViewerRef.current) {
      setError('Survey component not ready. Please try again.');
      return;
    }

    // Validate the survey first
    const validationError = assignmentViewerRef.current.validateSurvey();
    if (validationError) {
      setError(validationError);
      return;
    }

    // Get the survey data
    const surveyData = assignmentViewerRef.current.getSurveyData();
    
    setLoading(true);
    setError('');
    
    try {
      // Extract hours and instructor from survey data
      const hoursQuestion = surveyData.find(q => q._id === 'hours');
      const instructorQuestion = surveyData.find(q => q._id === 'instructor');
      
      const hours = hoursQuestion ? parseFloat(hoursQuestion.response) : 0;
      const instructor = instructorQuestion ? instructorQuestion.response : null;
      
      const api = createAxiosInstance();
      const response = await api.post('/api/openfeeventregistrations', {
         name: formData.name,
         grade: formData.grade,
         email: formData.email,
         feevent_id: formData.feevent_id,
         surveydata: surveyData,
         hours: hours,
         instructor: instructor,
         attendancetype: formData.attendancetype
       });
       
       setSuccess('Survey submitted successfully! Thank you for your participation.');
       setTimeout(() => {
         // Reset form and go back to first step
         setFormData({ name: '', grade: '', email: '', feevent_id: formData.feevent_id, attendancetype: 'Onsite' });
         setStep('form');
         setSuccess('');
       }, 3000);
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit survey. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToForm = () => {
    setStep('form');
    setError('');
  };

  // Admin mode functions
  const fetchSubmittedSurveys = async (eventId) => {
    try {
      const api = createAxiosInstance();
      const response = await api.get(`/api/openfeeventregistrations/by-event/${eventId}`);
      setSubmittedSurveys(response.data.registrations || []);
      
      // Clear stored original dates when data is refreshed
      setOriginalUpdatedDates({});
    } catch (err) {
      setError('Failed to load submitted surveys.');
    }
  };

  const fetchJobs = async (eventId) => {
    try {
      console.log('Fetching jobs for event:', eventId);
      const api = createAxiosInstance();
      const response = await api.get(`/api/openfeeventregistrations/jobsbyevent/${eventId}`);
      console.log('Jobs response:', response.data);
      setJobs(response.data.jobsbyevent || []);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      const errorMessage = err.response?.data?.details || err.response?.data?.error || 'Failed to load jobs.';
      setError(`Failed to load jobs: ${errorMessage}`);
    }
  };

  const handleAttendanceChange = async (surveyId, attendanceType) => {
    setUpdatingSurvey(surveyId);
    try {
      const survey = submittedSurveys.find(s => s.id === surveyId);
      const updatedSurvey = {
        ...survey,
        present: attendanceType !== 'Absent',
        attendancetype: attendanceType === 'Absent' ? null : attendanceType,
        currentUpdatedDate: survey.updateddate
      };
      
      const api = createAxiosInstance();
      const response = await api.put(`/api/openfeeventregistrations/${surveyId}`, updatedSurvey);
      
      // Update local state with the response data to get the new updateddate
      setSubmittedSurveys(prev => 
        prev.map(survey => 
          survey.id === surveyId 
            ? { ...survey, ...response.data.registration }
            : survey
        )
      );
      
      setSuccess('Attendance updated successfully!');
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Registration has been modified by another user. Please refresh and try again.');
        await fetchSubmittedSurveys(selectedEvent.id);
      } else {
        setError('Failed to update attendance.');
      }
    } finally {
      setUpdatingSurvey(null);
    }
  };

  const handleJobChange = async (surveyId, jobId) => {
    setUpdatingSurvey(surveyId);
    try {
      const survey = submittedSurveys.find(s => s.id === surveyId);
      const updatedSurvey = {
        ...survey,
        job: jobId,
        currentUpdatedDate: survey.updateddate
      };
      
      const api = createAxiosInstance();
      const response = await api.put(`/api/openfeeventregistrations/${surveyId}`, updatedSurvey);
      
      // Update local state with the response data to get the new updateddate
      setSubmittedSurveys(prev => 
        prev.map(survey => 
          survey.id === surveyId 
            ? { ...survey, ...response.data.registration }
            : survey
        )
      );
      
      setSuccess('Job assignment updated successfully!');
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Registration has been modified by another user. Please refresh and try again.');
        await fetchSubmittedSurveys(selectedEvent.id);
      } else {
        setError('Failed to update job assignment.');
      }
    } finally {
      setUpdatingSurvey(null);
    }
  };

  const handleHoursChange = (surveyId, hours) => {
    // Store original updateddate if not already stored
    setOriginalUpdatedDates(prev => {
      if (!prev[surveyId]) {
        const survey = submittedSurveys.find(s => s.id === surveyId);
        if (survey) {
          return { ...prev, [surveyId]: survey.updateddate };
        }
      }
      return prev;
    });
    
    // Store original hours value if not already stored (before any local changes)
    setOriginalFieldValues(prev => {
      if (!prev[surveyId]) {
        const survey = submittedSurveys.find(s => s.id === surveyId);
        if (survey) {
          return { 
            ...prev, 
            [surveyId]: { 
              ...prev[surveyId],
              hours: survey.hours || 0 
            } 
          };
        }
      }
      return prev;
    });
    
    // Update local state immediately for responsive UI
    setSubmittedSurveys(prev => 
      prev.map(survey => 
        survey.id === surveyId 
          ? { ...survey, hours: parseFloat(hours) || 0 }
          : survey
      )
    );
  };

  const handleHoursBlur = async (surveyId) => {
    // Get the current hours value
    const currentHours = submittedSurveys.find(s => s.id === surveyId)?.hours || 0;
    
    // Get the original hours value (before editing started)
    const originalHoursValue = originalFieldValues[surveyId]?.hours || 0;
    
    // Compare current hours with original hours value
    if (currentHours === originalHoursValue) {
      // No change detected, just clear the stored values and return
      setOriginalFieldValues(prev => {
        const newValues = { ...prev };
        delete newValues[surveyId];
        return newValues;
      });
      setOriginalUpdatedDates(prev => {
        const newDates = { ...prev };
        delete newDates[surveyId];
        return newDates;
      });
      return;
    }
    
    await saveHoursToServer(surveyId);
  };

  const handleHoursKeyDown = async (surveyId, event) => {
    // Save on Enter, Return, Tab, or Escape
    if (event.key === 'Enter' || event.key === 'Return' || event.key === 'Tab' || event.key === 'Escape') {
      event.preventDefault();
      
      // Get the current hours value
      const currentHours = submittedSurveys.find(s => s.id === surveyId)?.hours || 0;
      
      // Get the original hours value (before editing started)
      const originalHoursValue = originalFieldValues[surveyId]?.hours || 0;
      
      // Compare current hours with original hours value
      if (currentHours === originalHoursValue) {
        // No change detected, just clear the stored values and return
        setOriginalFieldValues(prev => {
          const newValues = { ...prev };
          delete newValues[surveyId];
          return newValues;
        });
        setOriginalUpdatedDates(prev => {
          const newDates = { ...prev };
          delete newDates[surveyId];
          return newDates;
        });
        
        // If it's Tab or Escape, let the default behavior happen (blur/focus change)
        if (event.key === 'Tab' || event.key === 'Escape') {
          return;
        }
        
        // For Enter/Return, blur the input to move focus away
        event.target.blur();
        return;
      }
      
      await saveHoursToServer(surveyId);
      
      // If it's Tab or Escape, let the default behavior happen (blur/focus change)
      if (event.key === 'Tab' || event.key === 'Escape') {
        return;
      }
      
      // For Enter/Return, blur the input to move focus away
      event.target.blur();
    }
  };

  const saveHoursToServer = async (surveyId) => {
    const survey = submittedSurveys.find(s => s.id === surveyId);
    if (!survey) return;
    
    setUpdatingSurvey(surveyId);
    try {
      const originalDate = originalUpdatedDates[surveyId] || survey.updateddate;
      
      const updatedSurvey = {
        ...survey,
        hours: survey.hours || 0,
        currentUpdatedDate: originalDate
      };
      
      const api = createAxiosInstance();
      const response = await api.put(`/api/openfeeventregistrations/${surveyId}`, updatedSurvey);
      setSuccess('Hours updated successfully!');
      
      // Update local state with the response data to get the new updateddate
      setSubmittedSurveys(prev => 
        prev.map(survey => 
          survey.id === surveyId 
            ? { ...survey, ...response.data.registration }
            : survey
        )
      );
      
      // Clear the stored original values after successful update
      setOriginalFieldValues(prev => {
        const newValues = { ...prev };
        delete newValues[surveyId];
        return newValues;
      });
      setOriginalUpdatedDates(prev => {
        const newDates = { ...prev };
        delete newDates[surveyId];
        return newDates;
      });
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Registration has been modified by another user. Please refresh and try again.');
        await fetchSubmittedSurveys(selectedEvent.id);
      } else {
        setError('Failed to update hours.');
        // Revert local state on error
        await fetchSubmittedSurveys(selectedEvent.id);
      }
      
      // Clear the stored original values on error
      setOriginalFieldValues(prev => {
        const newValues = { ...prev };
        delete newValues[surveyId];
        return newValues;
      });
      setOriginalUpdatedDates(prev => {
        const newDates = { ...prev };
        delete newDates[surveyId];
        return newDates;
      });
    } finally {
      setUpdatingSurvey(null);
    }
  };

  const getAttendanceStatus = (survey) => {
    if (survey.present === false) {
      return { text: '❌ Absent', color: '#dc3545' };
    } else if (survey.present === true) {
      return { 
        text: `✅ Present (${survey.attendancetype || 'Unknown'})`, 
        color: '#28a745' 
      };
    } else {
      return { text: 'Not Marked', color: '#6c757d' };
    }
  };

  const getJobStatus = (survey) => {
    if (survey.job) {
      const job = jobs.find(j => j.id === survey.job);
      return { text: job ? job.title : 'Unknown Job', color: '#28a745' };
    } else {
      return { text: 'No Job Assigned', color: '#ffc107' };
    }
  };

  const getHoursStatus = (survey) => {
    const hours = survey.hours || 0;
    if (hours > 0) {
      return { text: `${hours} hour${hours !== 1 ? 's' : ''}`, color: '#28a745' };
    } else {
      return { text: 'No hours', color: '#ffc107' };
    }
  };

  // Sort surveys by name
  const getSortedSurveys = () => {
    return [...submittedSurveys].sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      if (sortOrder === 'asc') {
        return nameA.localeCompare(nameB);
      } else {
        return nameB.localeCompare(nameA);
      }
    });
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const handleDeleteRegistration = async (surveyId) => {
    if (!window.confirm('Are you sure you want to delete this registration? This action cannot be undone.')) {
      return;
    }
    
    setUpdatingSurvey(surveyId);
    try {
      const api = createAxiosInstance();
      await api.delete(`/api/openfeeventregistrations/${surveyId}`);
      
      // Remove from local state
      setSubmittedSurveys(prev => prev.filter(survey => survey.id !== surveyId));
      setSuccess('Registration deleted successfully!');
    } catch (err) {
      setError('Failed to delete registration.');
    } finally {
      setUpdatingSurvey(null);
    }
  };

  const handleFieldEdit = (surveyId, field, value) => {
    // Store original updateddate if not already stored
    setOriginalUpdatedDates(prev => {
      if (!prev[surveyId]) {
        const survey = submittedSurveys.find(s => s.id === surveyId);
        if (survey) {
          return { ...prev, [surveyId]: survey.updateddate };
        }
      }
      return prev;
    });
    
    // Store original field values if not already stored (before any local changes)
    setOriginalFieldValues(prev => {
      if (!prev[surveyId]) {
        const survey = submittedSurveys.find(s => s.id === surveyId);
        if (survey) {
          return { 
            ...prev, 
            [surveyId]: { 
              name: survey.name || '', 
              email: survey.email || '' 
            } 
          };
        }
      }
      return prev;
    });
    
    // Update local state immediately for responsive UI
    setSubmittedSurveys(prev => 
      prev.map(survey => 
        survey.id === surveyId 
          ? { ...survey, [field]: value }
          : survey
      )
    );
  };

  const handleFieldBlur = async (surveyId, field) => {
    // Get the current input value
    const currentValue = submittedSurveys.find(s => s.id === surveyId)?.[field] || '';
    
    // Get the original field value (before editing started)
    const originalFieldValue = originalFieldValues[surveyId]?.[field] || '';
    
    // Compare current input value with original field value
    if (currentValue === originalFieldValue) {
      // No change detected, just clear the stored values and return
      setOriginalFieldValues(prev => {
        const newValues = { ...prev };
        delete newValues[surveyId];
        return newValues;
      });
      setOriginalUpdatedDates(prev => {
        const newDates = { ...prev };
        delete newDates[surveyId];
        return newDates;
      });
      return;
    }
    
    await saveFieldToServer(surveyId, field);
  };

  const handleFieldKeyDown = async (surveyId, field, event) => {
    // Save on Enter, Return, Tab, or Escape
    if (event.key === 'Enter' || event.key === 'Return' || event.key === 'Tab' || event.key === 'Escape') {
      event.preventDefault();
      
      // Get the current input value
      const currentValue = submittedSurveys.find(s => s.id === surveyId)?.[field] || '';
      
      // Get the original field value (before editing started)
      const originalFieldValue = originalFieldValues[surveyId]?.[field] || '';
      
      // Compare current value with original field value
      if (currentValue === originalFieldValue) {
        // No change detected, just clear the stored values and return
        setOriginalFieldValues(prev => {
          const newValues = { ...prev };
          delete newValues[surveyId];
          return newValues;
        });
        setOriginalUpdatedDates(prev => {
          const newDates = { ...prev };
          delete newDates[surveyId];
          return newDates;
        });
        
        // If it's Tab or Escape, let the default behavior happen (blur/focus change)
        if (event.key === 'Tab' || event.key === 'Escape') {
          return;
        }
        
        // For Enter/Return, blur the input to move focus away
        event.target.blur();
        return;
      }
      
      await saveFieldToServer(surveyId, field);
      
      // If it's Tab or Escape, let the default behavior happen (blur/focus change)
      if (event.key === 'Tab' || event.key === 'Escape') {
        return;
      }
      
      // For Enter/Return, blur the input to move focus away
      event.target.blur();
    }
  };

  const saveFieldToServer = async (surveyId, field) => {
    const survey = submittedSurveys.find(s => s.id === surveyId);
    if (!survey) return;
    
    setUpdatingSurvey(surveyId);
    try {
      const originalDate = originalUpdatedDates[surveyId] || survey.updateddate;
      
      // Only send the specific field being updated
      const updateData = {
        [field]: survey[field] || '',
        currentUpdatedDate: originalDate
      };
      
      const api = createAxiosInstance();
      const response = await api.put(`/api/openfeeventregistrations/${surveyId}`, updateData);
      setSuccess(`${field === 'name' ? 'Name' : 'Email'} updated successfully!`);
      
      // Update local state with the response data to get the new updateddate
      setSubmittedSurveys(prev => 
        prev.map(survey => 
          survey.id === surveyId 
            ? { ...survey, ...response.data.registration }
            : survey
        )
      );
      
      // Clear the stored original values after successful update
      setOriginalFieldValues(prev => {
        const newValues = { ...prev };
        delete newValues[surveyId];
        return newValues;
      });
      setOriginalUpdatedDates(prev => {
        const newDates = { ...prev };
        delete newDates[surveyId];
        return newDates;
      });
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Registration has been modified by another user. Please refresh and try again.');
        await fetchSubmittedSurveys(selectedEvent.id);
      } else {
        setError(`Failed to update ${field === 'name' ? 'name' : 'email'}.`);
        // Revert local state on error
        await fetchSubmittedSurveys(selectedEvent.id);
      }
      
      // Clear the stored original values on error
      setOriginalFieldValues(prev => {
        const newValues = { ...prev };
        delete newValues[surveyId];
        return newValues;
      });
      setOriginalUpdatedDates(prev => {
        const newDates = { ...prev };
        delete newDates[surveyId];
        return newDates;
      });
    } finally {
      setUpdatingSurvey(null);
    }
  };

  // Handle redirect to login
  if (redirectToLogin) {
    const returnTo = location.pathname + location.search + location.hash;
    return <Navigate to="/login" state={{ from: returnTo }} replace />;
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '24px', color: '#333', fontSize: 28, textAlign: 'center' }}>
        {isAdminMode ? 'FE Event Attendance (ADMIN MODE)' : 'Field Experience Event Survey'}
      </h1>
      
      {error && (
        <div style={{
          color: '#d32f2f',
          background: '#ffebee',
          border: '1px solid #ffcdd2',
          borderRadius: 8,
          padding: '1rem',
          marginBottom: '1rem',
          fontWeight: 600
        }}>
          {error}
        </div>
      )}
      
      {success && (
        <div style={{
          color: '#2e7d32',
          background: '#e8f5e8',
          border: '1px solid #c8e6c9',
          borderRadius: 8,
          padding: '1rem',
          marginBottom: '1rem',
          fontWeight: 600
        }}>
          {success}
        </div>
      )}

             {!isAdminMode && (
         <>
           {step === 'form' ? (
             <div style={{
               background: '#fff',
               border: '2px solid #1976d2',
               borderRadius: 12,
               padding: '24px',
               boxShadow: '0 2px 12px #1976d220'
             }}>
               <h2 style={{ marginBottom: '20px', color: '#1976d2', fontSize: 20 }}>
                 Student Information
               </h2>
               
               <form onSubmit={handleFormSubmit}>
                 <div style={{ marginBottom: '20px' }}>
                   <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#333' }}>
                     Field Experience Event *
                   </label>
                   <select
                     value={formData.feevent_id}
                     onChange={(e) => handleEventChange(e.target.value)}
                     style={{
                       width: '100%',
                       padding: '12px',
                       border: '1px solid #ddd',
                       borderRadius: 6,
                       fontSize: 16
                     }}
                     required
                   >
                     <option value="">Select an event...</option>
                     {events.map(event => (
                       <option key={event.id} value={event.id}>
                         {formatDateToMMMDDYYYY(event.date)} - {event.title} ({event.type})
                       </option>
                     ))}
                   </select>
                 </div>

                 <div style={{ marginBottom: '20px' }}>
                   <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#333' }}>
                     Full Name *
                   </label>
                   <input
                     type="text"
                     value={formData.name}
                     onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                     style={{
                       width: '100%',
                       padding: '12px',
                       border: '1px solid #ddd',
                       borderRadius: 6,
                       fontSize: 16
                     }}
                     required
                   />
                 </div>

                 <div style={{ marginBottom: '20px' }}>
                   <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#333' }}>
                     Grade Level *
                   </label>
                   <select
                     value={formData.grade}
                     onChange={(e) => setFormData(prev => ({ ...prev, grade: e.target.value }))}
                     style={{
                       width: '100%',
                       padding: '12px',
                       border: '1px solid #ddd',
                       borderRadius: 6,
                       fontSize: 16
                     }}
                     required
                   >
                     <option value="">Select grade level...</option>
                     <option value="TK">TK</option>
                     <option value="K">K</option>
                     <option value="1st">1st</option>
                     <option value="2nd">2nd</option>
                     <option value="3rd">3rd</option>
                     <option value="4th">4th</option>
                     <option value="5th">5th</option>
                     <option value="6th">6th</option>
                     <option value="7th">7th</option>
                     <option value="8th">8th</option>
                     <option value="9th">9th</option>
                     <option value="10th">10th</option>
                     <option value="11th">11th</option>
                     <option value="12th">12th</option>
                     <option value="Adult Age Learners">Adult Age Learners</option>
                   </select>
                 </div>

                 <div style={{ marginBottom: '20px' }}>
                   <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#333' }}>
                     Email Address
                   </label>
                   <input
                     type="email"
                     value={formData.email}
                     onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                     style={{
                       width: '100%',
                       padding: '12px',
                       border: '1px solid #ddd',
                       borderRadius: 6,
                       fontSize: 16
                     }}
                   />
                 </div>

                 <div style={{ marginBottom: '24px' }}>
                   <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#333' }}>
                     I attended the event
                   </label>
                   <div style={{ display: 'flex', gap: '16px' }}>
                     <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                       <input
                         type="radio"
                         name="attendancetype"
                         value="Onsite"
                         checked={formData.attendancetype === 'Onsite'}
                         onChange={(e) => setFormData(prev => ({ ...prev, attendancetype: e.target.value }))}
                         style={{ cursor: 'pointer' }}
                       />
                       <span>In Person</span>
                     </label>
                     <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                       <input
                         type="radio"
                         name="attendancetype"
                         value="Virtual"
                         checked={formData.attendancetype === 'Virtual'}
                         onChange={(e) => setFormData(prev => ({ ...prev, attendancetype: e.target.value }))}
                         style={{ cursor: 'pointer' }}
                       />
                       <span>Virtually (not In Person)</span>
                     </label>
                   </div>
                 </div>

                 <button
                   type="submit"
                   style={{
                     width: '100%',
                     padding: '14px',
                     background: '#1976d2',
                     color: 'white',
                     border: 'none',
                     borderRadius: 8,
                     fontSize: 16,
                     fontWeight: 600,
                     cursor: 'pointer',
                     transition: 'background 0.2s'
                   }}
                   onMouseOver={(e) => e.target.style.background = '#1565c0'}
                   onMouseOut={(e) => e.target.style.background = '#1976d2'}
                 >
                   Continue to Survey
                 </button>
               </form>
             </div>
           ) : (
             <div style={{
               background: '#fff',
               border: '2px solid #1976d2',
               borderRadius: 12,
               padding: '24px',
               boxShadow: '0 2px 12px #1976d220'
             }}>
               <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                 <h2 style={{ color: '#1976d2', fontSize: 20, margin: 0 }}>
                   Event Survey
                 </h2>
                 <button
                   onClick={handleBackToForm}
                   style={{
                     padding: '8px 16px',
                     background: '#f5f5f5',
                     color: '#333',
                     border: '1px solid #ddd',
                     borderRadius: 6,
                     cursor: 'pointer',
                     fontSize: 14
                   }}
                 >
                   ← Back to Form
                 </button>
               </div>
               
               {assignment ? (
                 <div>
                   <AssignmentViewer
                     ref={assignmentViewerRef}
                     assignment={assignment}
                     mode="preview"
                     interactive={true}
                     instructorChoices={instructors.map(instructor => ({
                       name: instructor.full_name,
                       account_id: instructor.account_id
                     }))}
                     eventDetails={{
                       title: selectedEvent?.title,
                       date: selectedEvent?.date,
                       start_time: selectedEvent?.start_time,
                       end_time: selectedEvent?.end_time,
                       location: selectedEvent?.location
                     }}
                     hideSubmitButton={true}
                   />
                   <div style={{ marginTop: '24px', textAlign: 'center' }}>
                     <button
                       onClick={handleSurveySubmit}
                       disabled={loading}
                       style={{
                         padding: '14px 28px',
                         background: '#1976d2',
                         color: 'white',
                         border: 'none',
                         borderRadius: 8,
                         fontSize: 16,
                         fontWeight: 600,
                         cursor: loading ? 'not-allowed' : 'pointer',
                         transition: 'background 0.2s',
                         opacity: loading ? 0.7 : 1
                       }}
                       onMouseOver={(e) => !loading && (e.target.style.background = '#1565c0')}
                       onMouseOut={(e) => !loading && (e.target.style.background = '#1976d2')}
                     >
                       {loading ? 'Submitting...' : 'Submit Survey'}
                     </button>
                   </div>
                 </div>
               ) : (
                 <div style={{ textAlign: 'center', padding: '40px' }}>
                   <LoadingSpinner />
                   <p style={{ marginTop: '16px', color: '#666' }}>Loading survey...</p>
                 </div>
               )}
             </div>
           )}
         </>
       )}

      {/* Admin Mode View */}
      {isAdminMode && !isAuthenticated() && (
        <div style={{
          background: '#fff',
          border: '2px solid #dc3545',
          borderRadius: 12,
          padding: '24px',
          boxShadow: '0 2px 12px #dc354520'
        }}>
          <h2 style={{ marginBottom: '20px', color: '#dc3545', fontSize: 20 }}>
            Authentication Required
          </h2>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            You must be logged in to access admin mode. Please log in to continue.
          </p>
          <button
            onClick={() => setRedirectToLogin(true)}
            style={{
              padding: '12px 24px',
              background: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.target.style.background = '#c82333'}
            onMouseOut={(e) => e.target.style.background = '#dc3545'}
          >
            Go to Login
          </button>
        </div>
      )}

      {isAdminMode && isAuthenticated() && (
        <div style={{
          background: '#fff',
          border: '2px solid #1976d2',
          borderRadius: 12,
          padding: '24px',
          boxShadow: '0 2px 12px #1976d220'
        }}>
          <h2 style={{ marginBottom: '20px', color: '#1976d2', fontSize: 20 }}>
            Admin Panel
          </h2>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#333' }}>
              Select Event
            </label>
            <select
              value={formData.feevent_id}
              onChange={(e) => handleEventChange(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: 6,
                fontSize: 16
              }}
            >
              <option value="">Choose an event...</option>
              {events.map(event => (
                <option key={event.id} value={event.id}>
                  {formatDateToMMMDDYYYY(event.date)} - {event.title} ({event.type})
                </option>
              ))}
            </select>
          </div>

                     {selectedEvent && (
             <div style={{ marginBottom: '20px' }}>
               <h3 style={{ marginBottom: '10px', color: '#333' }}>
                 Event: {selectedEvent.title} ({formatDateToMMMDDYYYY(selectedEvent.date)})
               </h3>
               <p style={{ color: '#666', marginBottom: '10px' }}>
                 {submittedSurveys.length} survey{submittedSurveys.length !== 1 ? 's' : ''} submitted
               </p>
               <p style={{ color: '#666', marginBottom: '10px', fontSize: '12px' }}>
                 Jobs loaded: {jobs.length} (Debug info)
               </p>
             </div>
           )}

          {submittedSurveys.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ color: '#333', margin: 0 }}>Submitted Surveys</h3>
                <button
                  onClick={toggleSortOrder}
                  style={{
                    padding: '8px 16px',
                    background: '#f5f5f5',
                    color: '#333',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  Sort by Name {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {getSortedSurveys().map(survey => (
                  <div key={survey.id} style={{
                    border: '1px solid #ddd',
                    borderRadius: 8,
                    padding: '16px',
                    background: '#f9f9f9',
                    position: 'relative'
                  }}>
                    {/* Delete button */}
                    <button
                      onDoubleClick={() => handleDeleteRegistration(survey.id)}
                      disabled={updatingSurvey === survey.id}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: 'none',
                        border: 'none',
                        color: '#dc3545',
                        fontSize: '16px',
                        cursor: 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        fontWeight: 'bold',
                        opacity: updatingSurvey === survey.id ? 0.5 : 1
                      }}
                      title="Double-click to delete registration"
                    >
                      ×
                    </button>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div style={{ flex: 1, marginRight: '20px' }}>
                        {/* Editable Name */}
                        <div style={{ marginBottom: '4px' }}>
                          <input
                            type="text"
                            value={survey.name || ''}
                            onChange={(e) => handleFieldEdit(survey.id, 'name', e.target.value)}
                            onKeyDown={(e) => handleFieldKeyDown(survey.id, 'name', e)}
                            disabled={updatingSurvey === survey.id}
                            style={{
                              width: '100%',
                              padding: '4px 8px',
                              border: '1px solid transparent',
                              borderRadius: '4px',
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#333',
                              background: 'transparent',
                              cursor: 'text'
                            }}
                            onFocus={(e) => {
                              e.target.style.border = '1px solid #1976d2';
                              e.target.style.background = '#fff';
                            }}
                            onBlur={(e) => {
                              e.target.style.border = '1px solid transparent';
                              e.target.style.background = 'transparent';
                              handleFieldBlur(survey.id, 'name');
                            }}
                          />
                        </div>
                        
                        {/* Editable Email */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ color: '#666', fontSize: '14px' }}>
                            {survey.grade} Grade •
                          </span>
                          <input
                            type="email"
                            value={survey.email || ''}
                            onChange={(e) => handleFieldEdit(survey.id, 'email', e.target.value)}
                            onKeyDown={(e) => handleFieldKeyDown(survey.id, 'email', e)}
                            disabled={updatingSurvey === survey.id}
                            placeholder="No email"
                            style={{
                              flex: 1,
                              padding: '2px 6px',
                              border: '1px solid transparent',
                              borderRadius: '4px',
                              fontSize: '14px',
                              color: '#666',
                              background: 'transparent',
                              cursor: 'text'
                            }}
                            onFocus={(e) => {
                              e.target.style.border = '1px solid #1976d2';
                              e.target.style.background = '#fff';
                            }}
                            onBlur={(e) => {
                              e.target.style.border = '1px solid transparent';
                              e.target.style.background = 'transparent';
                              handleFieldBlur(survey.id, 'email');
                            }}
                          />
                        </div>
                      </div>
                      <div style={{ fontSize: '12px', color: '#888' }}>
                        {new Date(survey.createddate || survey.id).toLocaleString()}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                      {/* Attendance */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, color: '#333', fontSize: '14px' }}>
                          Attendance
                        </label>
                        <select
                          value={survey.present === false ? 'Absent' : (survey.attendancetype || '')}
                          onChange={(e) => handleAttendanceChange(survey.id, e.target.value)}
                          disabled={updatingSurvey === survey.id}
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: 4,
                            fontSize: '14px'
                          }}
                        >
                          <option value="Onsite">In Person</option>
                          <option value="Virtual">Virtual</option>
                          <option value="Absent">Absent</option>
                        </select>
                      </div>

                      {/* Job Assignment */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, color: '#333', fontSize: '14px' }}>
                          Job Assignment
                        </label>
                        <select
                          value={survey.job || ''}
                          onChange={(e) => handleJobChange(survey.id, e.target.value)}
                          disabled={updatingSurvey === survey.id}
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: 4,
                            fontSize: '14px'
                          }}
                        >
                          <option value="">No Job Assigned</option>
                          {jobs.length > 0 ? (
                            jobs.map(job => (
                              <option key={job.id} value={job.id}>
                                {job.title}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>No jobs available</option>
                          )}
                        </select>
                      </div>

                      {/* Hours */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, color: '#333', fontSize: '14px' }}>
                          Hours
                        </label>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          value={survey.hours || ''}
                          onChange={(e) => handleHoursChange(survey.id, e.target.value)}
                          onBlur={() => handleHoursBlur(survey.id)}
                          onKeyDown={(e) => handleHoursKeyDown(survey.id, e)}
                          disabled={updatingSurvey === survey.id}
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: 4,
                            fontSize: '14px'
                          }}
                          placeholder="0.0"
                        />
                      </div>
                    </div>

                    {/* Current Status */}
                    <div style={{ marginTop: '12px', display: 'flex', gap: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: getAttendanceStatus(survey).color + '20',
                        color: getAttendanceStatus(survey).color
                      }}>
                        {getAttendanceStatus(survey).text}
                      </span>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: getJobStatus(survey).color + '20',
                        color: getJobStatus(survey).color
                      }}>
                        {getJobStatus(survey).text}
                      </span>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: getHoursStatus(survey).color + '20',
                        color: getHoursStatus(survey).color
                      }}>
                        {getHoursStatus(survey).text}
                      </span>
                    </div>

                    {updatingSurvey === survey.id && (
                      <div style={{ marginTop: '8px', color: '#1976d2', fontSize: '12px' }}>
                        Updating...
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedEvent && submittedSurveys.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              No surveys submitted for this event yet.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OpenFEEventSurvey; 